import { useEffect, useRef, type ReactNode } from "react";

interface DialogFrameProps {
	open: boolean;
	title: string;
	width: number;
	/** Escape で閉じる。処理中など閉じられないときは呼び出し側で無視する。 */
	onClose: () => void;
	/** 指定すると本文とフッターを form で包み、submit をこのハンドラへ渡す。 */
	onSubmit?: (event: React.FormEvent) => void;
	children: ReactNode;
	/** null なら本文の操作だけで閉じさせ、フッターの帯ごと出さない。 */
	footer: ReactNode | null;
}

/** 管理画面のモーダル共通枠 (見出し・本文・フッター)。showModal の開閉と Escape を面倒みる。 */
export function DialogFrame({
	open,
	title,
	width,
	onClose,
	onSubmit,
	children,
	footer,
}: DialogFrameProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const element = dialogRef.current;
		if (!element) return;
		if (open && !element.open) element.showModal();
		else if (!open && element.open) element.close();
	}, [open]);

	if (!open) return null;

	const body = (
		<>
			<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
				<h3
					style={{
						margin: 0,
						fontSize: "var(--text-title)",
						fontWeight: 600,
						color: "var(--fg-1)",
						fontFamily: "var(--font-sans)",
					}}
				>
					{title}
				</h3>
			</div>
			<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
				{children}
			</div>
			{footer !== null && (
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						borderTop: "var(--pane-header-border)",
						padding: "12px 24px",
					}}
				>
					{footer}
				</div>
			)}
		</>
	);

	return (
		<dialog
			ref={dialogRef}
			className="backdrop:bg-scrim"
			onKeyDown={(event) => {
				if (event.key !== "Escape") return;
				event.preventDefault();
				onClose();
			}}
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width,
			}}
		>
			{onSubmit ? <form onSubmit={onSubmit}>{body}</form> : body}
		</dialog>
	);
}
